// Mitarbeiter-Verwaltung: Profile-Liste mit Rollen + Schichten-Freigabe

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  useAllShifts, useApproveShift, useRejectShift,
  shiftDurationMinutes, STATUS_SHIFT_LABEL, STATUS_SHIFT_PILL,
} from "@/lib/shifts";

const dfmt = new Intl.DateTimeFormat("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

type Role = "admin" | "buero" | "dispatch" | "fahrer" | "kunde_privat" | "kunde_edeka";
const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin", buero: "Büro", dispatch: "Dispatch", fahrer: "Fahrer",
  kunde_privat: "Kunde (Privat)", kunde_edeka: "Kunde (EDEKA)",
};

const STAFF_ROLES: Role[] = ["admin", "buero", "dispatch", "fahrer"];

export default function MitarbeiterPage() {
  const [tab, setTab] = useState<"liste" | "schichten">("liste");
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold text-hof-900">Mitarbeiter</h1>
      <nav className="flex gap-1 border-b border-hof-200">
        <Tab active={tab === "liste"} onClick={() => setTab("liste")}>Mitarbeiterliste</Tab>
        <Tab active={tab === "schichten"} onClick={() => setTab("schichten")}>Schichten zur Freigabe</Tab>
      </nav>
      {tab === "liste" ? <MitarbeiterListe /> : <SchichtenFreigabe />}
    </div>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-3 -mb-px border-b-2 ${active ? "border-wiese-600 text-wiese-700 font-medium" : "border-transparent text-hof-600 hover:text-hof-900"}`}>
      {children}
    </button>
  );
}

function MitarbeiterListe() {
  const qc = useQueryClient();
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["profiles-staff"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, role, full_name, phone")
        .in("role", STAFF_ROLES)
        .order("role")
        .order("full_name");
      if (error) throw error;
      return data ?? [];
    },
  });
  const updateRole = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: Role }) => {
      const { error } = await supabase.from("profiles").update({ role }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles-staff"] }),
  });

  if (isLoading) return <div className="card">Lade…</div>;
  if (profiles.length === 0) return <div className="card text-center text-hof-600">Keine Mitarbeiter angelegt.</div>;

  return (
    <div className="card p-0 overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-hof-50 text-sm text-hof-600">
          <tr>
            <th className="p-3">Name</th>
            <th className="p-3">Telefon</th>
            <th className="p-3">Rolle</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((p) => (
            <tr key={p.id} className="border-t border-hof-100">
              <td className="p-3 font-medium">{p.full_name ?? p.id.slice(0, 8)}</td>
              <td className="p-3 text-hof-700">{p.phone ?? "—"}</td>
              <td className="p-3">
                <select
                  className="input max-w-xs"
                  value={p.role}
                  onChange={(e) => updateRole.mutate({ id: p.id, role: e.target.value as Role })}
                >
                  {Object.entries(ROLE_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SchichtenFreigabe() {
  const { data: pending = [], isLoading } = useAllShifts({ status: "submitted" });
  const approve = useApproveShift();
  const reject = useRejectShift();

  if (isLoading) return <div className="card">Lade…</div>;
  if (pending.length === 0) return <div className="card text-center text-hof-600 py-6">Keine offenen Schichten.</div>;

  return (
    <div className="space-y-2">
      {pending.map((s) => {
        const dur = shiftDurationMinutes(s);
        return (
          <article key={s.id} className="card flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-medium text-hof-900">{s.driver?.full_name ?? s.driver_id.slice(0, 8)}</div>
              <div className="text-sm text-hof-700">{dfmt.format(new Date(s.started_at))} → {s.ended_at ? dfmt.format(new Date(s.ended_at)) : "—"}</div>
              <div className="text-sm text-hof-500">
                {Math.floor(dur/60)}h {String(dur%60).padStart(2,"0")}min · Pause {s.break_minutes}min
              </div>
              {s.notes && <div className="text-sm italic mt-1">"{s.notes}"</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => approve.mutate(s.id)} className="btn-primary text-sm">✓ Freigeben</button>
              <button onClick={() => {
                const reason = prompt("Ablehnungsgrund?");
                if (reason) reject.mutate({ id: s.id, reason });
              }} className="btn-danger text-sm">Ablehnen</button>
            </div>
            <div className="w-full"><span className={STATUS_SHIFT_PILL[s.status]}>{STATUS_SHIFT_LABEL[s.status]}</span></div>
          </article>
        );
      })}
    </div>
  );
}
